import { Component, Inject, Input, Output, EventEmitter } from "@angular/core";
import { combineLatest, forkJoin, Observable, of, Subject } from "rxjs";
import { mergeMap, tap } from "rxjs/operators";

import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from '../../entities/document-pa-button';
import { DocumentPersonneAvertie, CategoriePa, EtatDocumentPa } from "../../entities/document-pa.entity";
import { SubjectRelay } from "../../../../shared/commands/subject-relay.class";
import { ButtonActions, applicationConstants } from '../../../../app.constants';

import { IntervenantTLR } from "../../../humain/intervenant/entities/intervenant-tlr.entity";
import { EntrepriseTLR } from "../../../entreprises/entities/entreprise-tlr.entity";
import { UpdateStepEntrepriseOrder } from "../../entities/document-pa-update-step-entreprise-order.entity";
import { ParcTLR } from "../../../structure/parc/entities/parc-tlr.entity";

import { DocumentPaService } from "../../services/document-pa-service";
import { EntrepriseService } from "../../../entreprises/services/entreprise.service";
import { IntervenantService } from "../../../humain/intervenant/services/intervenant.service";
import { StaticTypeService } from "src/main/angular/app/static-types/services/static-types.service";
import { IndicatifTelephone } from "src/main/angular/app/static-types/entities/indicatif-telephone.entity";
import { AntenneService } from "../../../structure/antenne/services/antenne.service";
import { ParcService }from "../../../structure/parc/services/parc.service";

@Component({
	selector: 'document-pa-entreprises',
	templateUrl: 'document-pa-entreprises.html',
	styleUrls: ['document-pa-entreprises.css'],
})
export class DocumentPaEntreprisesComponent {

	@Output() actionRequested = new EventEmitter<StepAction>();

	mandatoryField: boolean = true;

	entrepriseUtilisatrice = {
		raisonSociale: '',
		nom: ''
	};

	entrepriseExterieure = {
		raisonSociale: '',
		nom: ''
	};

	actionButtons: DocumentPaButton[] = [
		new DocumentPaButton(ButtonActions.PREVIOUS, 'Back', 'btn btn-primary'),
		new DocumentPaButton(ButtonActions.CANCEL, 'Liste des PA', 'btn btn-danger'),
		new DocumentPaButton(ButtonActions.SAVE, 'Sauvegarder maintenant', 'btn btn-warning'),
		new DocumentPaButton(ButtonActions.NEXT, 'Next', 'btn btn-success'),
	];

	order !: UpdateStepEntrepriseOrder;
	entreprises !: EntrepriseTLR[];
	entreprisesExterieures !: EntrepriseTLR[];
	intervenants !: IntervenantTLR[];
	filteredEntreprisesEe: EntrepriseTLR[] = [];
	filteredIntervenantsEe: IntervenantTLR[] = [];
	selectedEntrepriseEe?: EntrepriseTLR | null;
	selectedIntervenantEe?: IntervenantTLR | null;
	entrepriseReferenteNom: string = '';

	paEtats: typeof EtatDocumentPa = EtatDocumentPa;
	
	currentPaValue!: DocumentPersonneAvertie;
	
	indicatifTelephoneOptions: IndicatifTelephone[] = [];
	
	getIndicatifLabel = (it: IndicatifTelephone) => it ? (it.name + ' (' + it.code + ') ' + it.dialCode) : '';
	
	getNumeroAstreinteLabel = (numeroAstreinte: string) => numeroAstreinte;
	initAstreinteCommand = new Subject<void>();
	numeroAstreintesCommand = new Subject<string[]>();
	existingAstreintesCommand = new Subject<string[]>();
	telephoneAstreinte: string[] = [];
	numeroAstreintes?: string[];
	isCreationMode: boolean = true;
	
	constructor(
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(DocumentPaService) private documentPaService: DocumentPaService,
		@Inject(AntenneService) private antenneService: AntenneService,
		@Inject(ParcService) private parcService: ParcService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService) {
			
		// Chargement des indicatifs de telephone
		let indicatifs$ = staticTypeService.getIndicatifTelephoneList();

		let entreprises$ = entrepriseService.getActiveEntreprisesTLRList();
		let intervenants$ = intervenantService.getIntervenantsTLRList();
		let parcs$ = parcService.getParcTLRMapById();
		let antennes$ = antenneService.getAntenneTLRMapById();

		let data$ = combineLatest(entreprises$, intervenants$, this.currentPa$, indicatifs$, parcs$, antennes$).pipe(
			mergeMap((data) => {
				this.indicatifTelephoneOptions = data[3];
				let step2Data$ = data[2] && data[2].id 
					? this.documentPaService.getStepEntrepriseData( data[2].duplicatedId ? data[2].duplicatedId : data[2].id ) 
					: of(null);
					return forkJoin({
						entreprises: of(data[0]),
						intervenants: of(data[1]),
						currentPa: of(data[2]),
						step2Data: step2Data$,
						parcs: of(data[4]),
						antennes: of(data[5])
					});
				}
			)
		);

		data$.subscribe((data) => {
			// Assign entreprises and intervenants first
			this.entreprises = data.entreprises;
			this.entreprisesExterieures = data.entreprises.filter(
				e => !e.nom.toUpperCase().startsWith("ENGIE GREEN") && (e.documentApplicable === "PersonneAvertie" || e.documentApplicable === "Both")
			);
			this.intervenants = data.intervenants.filter( i => !i.obsolete );
			
			let parcs = data.parcs;
			let antennes = data.antennes;

			const currentPa = data.currentPa as DocumentPersonneAvertie;
			this.currentPaValue = currentPa;
			const step2Data = data.step2Data as UpdateStepEntrepriseOrder | null;
			this.isCreationMode = !(step2Data && step2Data.id);
			
			let parcLabel = '';

			if (currentPa.parcIds && currentPa.parcIds.length) {
				const selectedParcs = currentPa.parcIds
					.map(id => parcs.get(id))
					.filter(p => !!p);

				parcLabel = selectedParcs.map(p => p!.code).join(', ');

				const parc = selectedParcs[0];

				if (parc) {
					const antenneId = parc.antenneId;
					const antenne = antennes.get(antenneId);

					this.numeroAstreintes = antenne?.telephoneAstreintes ?? [];
					this.initAstreinteCommand.next();
					this.numeroAstreintesCommand.next(this.numeroAstreintes);
				}
			}
			
			// Always prepare EE entreprise list first
			this.filteredEntreprisesEe = [...this.entreprisesExterieures];

			if (step2Data && step2Data.id) {
				// on a deja ajouté des entreprises à ce PA
				this.order = step2Data;
				this.order.id = currentPa.id; // par precaution on reaffecte le id du PA car en cas de duplication cet id est peut être mauvais dans step2Data

				// EU referent entreprise name
				const entrepriseEu = this.entreprises.find(e => e.id === this.order.idEntrepriseEu);
				if (entrepriseEu) {
					this.entrepriseReferenteNom = entrepriseEu.nom;
				}

				// EE entreprise & intervenant 
				if (this.order.idEntrepriseEe) {
					const selectedEe = this.entreprises.find(e => e.id === this.order.idEntrepriseEe);
					if (selectedEe) {
						this.selectedEntrepriseEe = selectedEe;
						this.order.raisonSocialeEe = step2Data.raisonSocialeEe;

						this.order.adresseEntrepriseEe = step2Data.adresseEntrepriseEe;
						this.order.nomPrenomResponsableEe = step2Data.nomPrenomResponsableEe;
						this.order.emailResponsableEe = step2Data.emailResponsableEe;
						this.order.fonctionResponsableEe = step2Data.fonctionResponsableEe;
						this.order.phoneResponsableEe = step2Data.phoneResponsableEe;
						this.order.eeEmailHasChanged = step2Data.eeEmailHasChanged;
						this.order.eePhoneHasChanged = step2Data.eePhoneHasChanged;
						this.order.emailResponsableEu = step2Data.emailResponsableEu;
						this.order.fonctionResponsableEu = step2Data.fonctionResponsableEu;
						this.order.phoneResponsableEu = step2Data.phoneResponsableEu;

						this.filteredIntervenantsEe = this.intervenants.filter(i => i.entrepriseId === selectedEe.id);
					}
				}

				if (this.order.idResponsableEe) {
					const interv = this.intervenants.find(i => i.id === this.order.idResponsableEe);
					if (interv) {
						this.selectedIntervenantEe = interv;
					}
				}

				// Reset change flags
				this.order.euEmailHasChanged = false;
				this.order.euPhoneHasChanged = false;
				this.order.eeEmailHasChanged = false;
				this.order.eePhoneHasChanged = false;

				if (!this.filteredIntervenantsEe || this.filteredIntervenantsEe.length === 0) {
					this.filteredIntervenantsEe = this.intervenants.filter(i => {
						const ent = this.entreprises.find(e => e.id === i.entrepriseId);
						return ent && !ent.nom.startsWith("Engie Green");
					});
				}
				
				if (step2Data.numeroAstreintesEu && step2Data.numeroAstreintesEu.length > 0) {
					this.order.numeroAstreintesEu = step2Data.numeroAstreintesEu;

					this.initAstreinteCommand.next();
					this.numeroAstreintesCommand.next(this.numeroAstreintes || []);
					this.existingAstreintesCommand.next(this.order.numeroAstreintesEu);
				}

			} else if (currentPa != undefined) {
			    // creation mode
			    this.order = new UpdateStepEntrepriseOrder();
			    this.order.id = currentPa.id;
				
				this.order.numeroAstreintesEu = [];

			    // Reset selected EE fields
			    this.selectedEntrepriseEe = null;
			    this.selectedIntervenantEe = null;

			    this.order.idEntrepriseEe = null;
			    this.order.raisonSocialeEe = '';
			    this.order.adresseEntrepriseEe = '';
			    this.order.idResponsableEe = null;
			    this.order.nomPrenomResponsableEe = '';
			    this.order.emailResponsableEe = '';
			    this.order.fonctionResponsableEe = '';
			    this.order.phoneResponsableEe = '';
			    this.order.indicatifResponsableEe = null;

			    // Reset change flags
			    this.order.euEmailHasChanged = false;
			    this.order.euPhoneHasChanged = false;
			    this.order.eeEmailHasChanged = false;
			    this.order.eePhoneHasChanged = false;

			    // Rebuild filtered EE intervenants list
			    this.filteredIntervenantsEe = this.intervenants.filter(
			        i => this.filteredEntreprisesEe.map(ee => ee.id).includes(i.entrepriseId as number)
			    );

			    // Populate EU referent as before
			    const responsable = this.intervenants.find(i => i.id === currentPa.responsableId);
			    if (responsable) {
			        this.order.idResponsableEu = responsable.id;
			        this.order.nomPrenomResponsableEu = `${responsable.nom} ${responsable.prenom}`;
			        this.order.emailResponsableEu = responsable.email ?? '';
			        this.order.fonctionResponsableEu = responsable.cadreEmploi ?? '';
			        this.order.phoneResponsableEu = responsable.telephone ?? '';

			        if (responsable.indicatifTelephone) {
			            const indicatif = this.indicatifTelephoneOptions.find(
			                i => i.dialCode === responsable.indicatifTelephone
			            );
			            if (indicatif) this.order.indicatifResponsableEu = indicatif.dialCode;
			        }

			        const entrepriseResponsable = this.entreprises.find(e => e.id === responsable.entrepriseId);
			        if (entrepriseResponsable) {
			            this.order.idEntrepriseEu = entrepriseResponsable.id;
			            this.entrepriseReferenteNom = entrepriseResponsable.nom;
			            this.order.adresseEntrepriseEu = applicationConstants.adresseEngieGreen;
			        }
			    }

			}
						
			if (parcLabel && !this.order.raisonSocialeEu?.trim()) {
				this.order.raisonSocialeEu = parcLabel;
			}

			// EE lists filtered
			if (this.selectedEntrepriseEe) {
				this.filteredIntervenantsEe = this.intervenants.filter(i => i.entrepriseId === this.selectedEntrepriseEe!.id);
			}
			this.evaluerFormulaire();
		});
	}

	private resetChangeFlags(): void {
		this.order.euEmailHasChanged = false;
		this.order.euPhoneHasChanged = false;
		this.order.eeEmailHasChanged = false;
		this.order.eePhoneHasChanged = false;
	}

	private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
	@Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
		this.currentPa$.registerSource(obs);
	}

	@Input() lastCompletedStep: number | null = null;

	onSelectEntrepriseEe(entreprise: EntrepriseTLR | null): void {
		if (!entreprise) {
			this.onClearEntrepriseEe();
			return;
		}

		this.selectedEntrepriseEe = entreprise;
		this.order.idEntrepriseEe = entreprise.id;
		this.order.raisonSocialeEe = entreprise.nom;

		const parts = [
			entreprise.adresseRue,
			entreprise.adresseCodePostal,
			entreprise.adresseVille,
		].filter(Boolean);
		this.order.adresseEntrepriseEe = parts.join(', ');

		this.filteredIntervenantsEe = this.intervenants.filter(
			(i) => i.entrepriseId === entreprise.id
		);

		this.selectedIntervenantEe = null;
		this.order.idResponsableEe = null;
		this.order.nomPrenomResponsableEe = '';
		this.order.emailResponsableEe = '';
		this.order.fonctionResponsableEe = '';
		this.order.phoneResponsableEe = '';

		this.order.eeEmailHasChanged = false;
		this.order.eePhoneHasChanged = false;
	}

	onSelectIntervenantEe(intervenant: IntervenantTLR | null): void {
		
		if (!intervenant) {
			this.selectedIntervenantEe = null;
			this.order.idResponsableEe = null;
			this.order.nomPrenomResponsableEe = '';
			this.order.emailResponsableEe = '';
			this.order.fonctionResponsableEe = '';
			this.order.phoneResponsableEe = '';
			this.evaluerFormulaire();
			return;
		}

		if (this.selectedIntervenantEe && this.selectedIntervenantEe.id === intervenant.id) {
			// on a pas changé d'intervenant
			// rien à faire
		} else {
			if (intervenant?.indicatifTelephone) {
				const indicatif = this.indicatifTelephoneOptions.find(
					i => i.dialCode === intervenant.indicatifTelephone
				);
				if (indicatif) {
					this.order.indicatifResponsableEe = indicatif.dialCode;
				}
			}
			this.selectedIntervenantEe = intervenant;
			this.order.idResponsableEe = intervenant.id;
			this.order.nomPrenomResponsableEe = `${intervenant.nom} ${intervenant.prenom ?? ''}`.trim();
			this.order.emailResponsableEe = intervenant.email ?? '';
			this.order.fonctionResponsableEe = intervenant.cadreEmploi ?? '';
			this.order.phoneResponsableEe = intervenant.telephone ?? '';
			this.order.eeEmailHasChanged = false;
			this.order.eePhoneHasChanged = false;

			// handle entreprise
			if (intervenant.entrepriseId) {
				if (this.selectedEntrepriseEe && this.selectedEntrepriseEe.id === intervenant.entrepriseId) {
					// on a pas changé d'entreprise
					// rien à faire
				} else {
					const entreprise = this.entreprises.find((e) => e.id === intervenant.entrepriseId);
					if (entreprise) {
						this.selectedEntrepriseEe = entreprise;
						this.order.idEntrepriseEe = entreprise.id;
						this.order.raisonSocialeEe = entreprise.nom;
						const parts = [
							entreprise.adresseRue,
							entreprise.adresseCodePostal,
							entreprise.adresseVille,
						].filter(Boolean);
						this.order.adresseEntrepriseEe = parts.join(', ');
	
						// Keep filtered intervenants relevant to this entreprise
						this.filteredIntervenantsEe = this.intervenants.filter(
							(i) => i.entrepriseId === entreprise.id
						);
					}					
				}
			}
			this.evaluerFormulaire();
		}
	}

	onClearEntrepriseEe(): void {
		this.selectedEntrepriseEe = null;
		this.order.idEntrepriseEe = null;
		this.order.raisonSocialeEe = '';
		this.order.adresseEntrepriseEe = '';
		this.selectedIntervenantEe = null;
		this.order.idResponsableEe = null;
		this.order.nomPrenomResponsableEe = '';
		this.order.emailResponsableEe = '';
		this.order.fonctionResponsableEe = '';
		this.order.indicatifResponsableEe = null;
		this.order.phoneResponsableEe = '';
		this.filteredIntervenantsEe = [];
		this.order.eeEmailHasChanged = false;
		this.order.eePhoneHasChanged = false;

		this.filteredIntervenantsEe = this.intervenants.filter(
			i => this.filteredEntreprisesEe.map(ee => ee.id).includes(i.entrepriseId as number)
		);
		
		this.evaluerFormulaire();
	}
	
	performAction(requestedAction: ButtonActions): void {
		console.log('requested action: ', requestedAction);

		switch (requestedAction) {
			case ButtonActions.CANCEL:
			case ButtonActions.RETURN_TO_LIST: {
				// return to list
				this.actionRequested.emit(new StepAction(null, ButtonActions.RETURN_TO_LIST));
				break;
			}

			case ButtonActions.PREVIOUS: {
				this.actionRequested.emit(new StepAction(this.currentPaValue as DocumentPersonneAvertie, ButtonActions.PREVIOUS));
				break;
			}

			case ButtonActions.SAVE:
			case ButtonActions.SAVE_DRAFT: {
				this.saveEntrepriseStep(false);
				break;
			}

			case ButtonActions.NEXT: {
				this.saveEntrepriseStep(true);
				break;
			}

			default:
				console.warn('Unhandled action:', requestedAction);
		}
	}

	private saveEntrepriseStep(next: boolean): void {
		if (this.isFormValid()) {
			this.documentPaService.updateEntrepriseStep(this.order)
				.pipe(tap(() => this.intervenantService.clearCaches()))
				.subscribe( {
					next: (res) => {
						this.order = res;
						this.resetChangeFlags();
		
						if (!next) {
							this.actionRequested.emit(new StepAction(null, ButtonActions.RETURN_TO_LIST));
						} else {
							if (this.currentPaValue) {
								this.currentPaValue.id = res.id;
								if (this.currentPaValue.lastCompletedStep < 2) {
									this.currentPaValue.lastCompletedStep = 2;
								}
							}
							this.actionRequested.emit(new StepAction(this.currentPaValue as DocumentPersonneAvertie, ButtonActions.NEXT));
						}
					},
					error: (err) => {
						console.error('Erreurs lors de la sauvegarde des informations du step entreprise, erreur: ', err);
					}
				} );
		} else {
			this.evaluerFormulaire();
		}
	}

	evaluerFormulaire(): void {
		const isFormValid = this.isFormValid();

		// Enable or disable Sauvegarder and NEXT buttons
		const saveButton = this.actionButtons.find(b => b.action === ButtonActions.SAVE);
		const nextButton = this.actionButtons.find(b => b.action === ButtonActions.NEXT);

		if (saveButton) saveButton.disabled = !isFormValid;
		if (nextButton) nextButton.disabled = !isFormValid;
	}

	private isFormValid(): boolean {
		return !!this.order?.raisonSocialeEu?.trim() &&
			!!this.order?.nomPrenomResponsableEu?.trim() &&
			!!this.order?.emailResponsableEu?.trim() &&
			!!this.order?.raisonSocialeEe?.trim() &&
			!!this.order?.nomPrenomResponsableEe?.trim() &&
			this.isValidEmail(this.order?.emailResponsableEu);
	}

	onEuEmailChange(): void {
		this.order.euEmailHasChanged = true;
	}

	onEuPhoneChange(): void {
		this.order.euPhoneHasChanged = true;
	}

	onEeEmailChange(): void {
		this.order.eeEmailHasChanged = true;
	}

	onEePhoneChange(): void {
		this.order.eePhoneHasChanged = true;
	}

	getEntrepriseLabel = (e: EntrepriseTLR | null): string => {
		return e ? `${e.nom}` : '';
	};

	getIntervenantLabel = (i: IntervenantTLR | null): string => {
		return i ? `${i.nom} ${i.prenom ?? ''}`.trim() : '';
	};

	get isRaisonSocialeEuFilled(): boolean {
		return !!this.order?.raisonSocialeEu?.trim();
	}

	get isEmailEuFilled(): boolean {
		return this.isValidEmail(this.order?.emailResponsableEu);
	}

	onRaisonSocialeEuChange(value: string): void {
		this.order.raisonSocialeEu = value?.trim() ?? '';
	}

	get isEntrepriseReferenteFilled(): boolean {
		return !!this.order?.raisonSocialeEu?.trim();
	}

	get isRaisonSocialeEeFilled(): boolean {
		return !!this.order?.raisonSocialeEe?.trim();
	}

	get isResponsableEuFilled(): boolean {
		return !!this.order?.nomPrenomResponsableEu?.trim();
	}

	get isResponsableEeFilled(): boolean {
		return !!this.order?.nomPrenomResponsableEe?.trim();
	}

	get isEmailEeFilled(): boolean {
		return this.isValidEmail(this.order?.emailResponsableEe);
	}

	get isEntrepriseExterieureFilled(): boolean {
		return !!this.order?.raisonSocialeEe?.trim();
	}

	onEntrepriseReferenteChange(value: string): void {
		this.order.raisonSocialeEu = value?.trim() ?? '';
		if (!this.isEntrepriseReferenteFilled) {
			this.order.raisonSocialeEu = '';
			this.order.adresseEntrepriseEu = '';
		}
	}
	
	getSelectedIndicatifEu(): IndicatifTelephone | null {
		return this.indicatifTelephoneOptions.find(
			i => i.dialCode === this.order?.indicatifResponsableEu
		) ?? null;
	}

	getSelectedIndicatifEe(): IndicatifTelephone | null {
		return this.indicatifTelephoneOptions.find(
			i => i.dialCode === this.order?.indicatifResponsableEe
		) ?? null;
	}
	
	onEuIndicatifChange(indicatif: IndicatifTelephone | null): void {
		if (!indicatif) return;

		this.order.indicatifResponsableEu = indicatif.dialCode;
		this.order.euPhoneHasChanged = true;
	}

	onEeIndicatifChange(indicatif: IndicatifTelephone | null): void {
		if (!indicatif) return;

		this.order.indicatifResponsableEe = indicatif.dialCode;
		this.order.eePhoneHasChanged = true;
	}
	
	private isValidEmail(email: string | null | undefined): boolean {
		return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
	}
	
	onNumeroAstreinteSelected(selectedNumeroAstreinte: string[]) {
		console.log('onNumeroAstreinteSelected: ', selectedNumeroAstreinte);
		this.telephoneAstreinte = selectedNumeroAstreinte;
		this.order.numeroAstreintesEu = selectedNumeroAstreinte;
	}
}
